import { gql, useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  CreatedTimingPlaceFragment,
  GetTimingPlacesByLabelDocument,
  GetTimingPlacesByLabelQuery,
  GetTimingPlacesByLabelQueryVariables,
  NewTimingPlaceFragment,
  TimingPatternTimingPlaceInsertInput,
  useInsertTimingPlaceMutation,
} from '../../../../../generated/graphql';
import { showDangerToast } from '../../../../../utils';

type CreateParams = {
  readonly input: NewTimingPlaceFragment;
};

type CreateChanges = {
  readonly timingPlaceToCreate: TimingPatternTimingPlaceInsertInput;
  readonly conflicts?: ReadonlyArray<CreatedTimingPlaceFragment>;
};

const GQL_NEW_TIMING_PLACE = gql`
  fragment NewTimingPlace on timing_pattern_timing_place {
    label
    description
  }
`;

const GQL_CREATED_TIMING_PLACE = gql`
  fragment CreatedTimingPlace on timing_pattern_timing_place {
    timing_place_id
    label
    description
  }
`;

const GQL_INSERT_TIMING_PLACE = gql`
  mutation InsertTimingPlace(
    $object: timing_pattern_timing_place_insert_input!
  ) {
    insert_timing_pattern_timing_place_one(object: $object) {
      ...CreatedTimingPlace
    }
  }
`;

const GQL_GET_TIMING_PLACES_BY_LABEL = gql`
  query GetTimingPlacesByLabel($label: String!) {
    timing_pattern_timing_place(where: { label: { _eq: $label } }) {
      ...CreatedTimingPlace
    }
  }
`;

export function useCreateTimingPlace() {
  const { t } = useTranslation();

  const apollo = useApolloClient();
  const [mutateFunction] = useInsertTimingPlaceMutation();

  const insertTimingPlaceMutation = async ({
    timingPlaceToCreate,
  }: CreateChanges) =>
    mutateFunction({ variables: { object: timingPlaceToCreate } });

  const getConflictingTimingPlaces = async (label: string) => {
    const existingTimingPlacesResult = await apollo.query<
      GetTimingPlacesByLabelQuery,
      GetTimingPlacesByLabelQueryVariables
    >({
      query: GetTimingPlacesByLabelDocument,
      variables: { label },
    });

    return existingTimingPlacesResult.data.timing_pattern_timing_place;
  };

  // prepare variables for mutation and validate if it's even allowed
  // try to produce a changeset that can be displayed on an explanatory UI
  const prepareCreate = async ({
    input,
  }: CreateParams): Promise<CreateChanges> => ({
    timingPlaceToCreate: input,
    conflicts: await getConflictingTimingPlaces(input.label),
  });

  // default handler that can be used to show error messages as toast
  // in case an exception is thrown
  const defaultErrorHandler = (err: Error) => {
    // if other error happened, show the generic error message
    showDangerToast(`${t(($) => $.errors.saveFailed)}, ${err}, ${err.message}`);
  };

  return {
    prepareCreate,
    insertTimingPlaceMutation,
    defaultErrorHandler,
  };
}
